import React from "react";
import Dialog from "@components/Dialog";
import Input from "@components/Input";
import SizedBox from "@components/SizedBox";
import { Row } from "@components/Flex";
import Button from "@components/Button";
import { observer } from "mobx-react-lite";
import { useLang } from "@src/hooks/useLang";
import { authClient } from "@services/authService";
import { toast } from "react-toastify";

interface IProps {
  visible: boolean;
  onClose: () => void;
}

const ChangePasswordDialog: React.FC<IProps> = ({ visible, onClose }) => {
  const lang = useLang();
  const t = (lang as any).auth ?? {};

  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [repeatPassword, setRepeatPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const reset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setRepeatPassword("");
    setError(null);
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== repeatPassword) {
      setError(t.passwordsDontMatch ?? "Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const { error } = await authClient.changePassword({
        currentPassword,
        newPassword,
        // Kick every other device — a password change usually means the old
        // one may be compromised.
        revokeOtherSessions: true,
      });
      if (error) {
        // better-auth returns 401 INVALID_PASSWORD for a wrong current password.
        setError(
          error.status === 401 || error.status === 400
            ? lang.profile.oldPasswordAlert
            : error.message || lang.profile.oldPasswordAlert
        );
        setLoading(false);
        return;
      }
      toast.success(lang.profile.newPasswordAlert);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Password change failed");
      setLoading(false);
    }
  };

  return (
    <Dialog
      style={{ width: 408 }}
      title={lang.profile.passChangeTitle}
      visible={visible}
      onClose={handleClose}
      error={error}
    >
      <form onSubmit={handleSubmit}>
        <Input
          label={lang.profile.passChangeOld}
          placeholder={lang.profile.passChangePlaceholder}
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
        <SizedBox height={16} />
        <Input
          label={lang.profile.passChangeNew}
          placeholder={lang.profile.passChangePlaceholder}
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
        <SizedBox height={16} />
        <Input
          label={lang.auth?.repeatPasswordTitle ?? "Repeat password"}
          placeholder={lang.profile.passChangePlaceholder}
          type="password"
          value={repeatPassword}
          onChange={(e) => setRepeatPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
        <SizedBox height={24} />
        <Row>
          <Button kind="outline" type="button" onClick={handleClose}>
            {lang.profile.passCancelButtton}
          </Button>
          <SizedBox width={16} />
          <Button type="submit" disabled={loading}>
            {lang.profile.passChangeButton}
          </Button>
        </Row>
      </form>
    </Dialog>
  );
};
export default observer(ChangePasswordDialog);
